/**
 * Cookie Consent Entrypoint
 *
 * Vite entrypoint that runs in BOTH Rails layouts
 * (`application.html.erb` for the React SPA and
 * `authentication.html.erb` for Devise views). Because it runs before
 * React boots, it is what guarantees the first-visit surface renders on
 * first visit even when the landing page is `/login` or `/register`.
 *
 * Responsibilities:
 *   0. Move the post-auth `?t=<jwt>` into localStorage and off the URL,
 *      so gtm.js never observes the token.
 *   1. Expose the core as `window.__consent__` for ERB templates, then
 *      initialize the framework-agnostic consent core.
 *   1a. Bootstrap GTM Consent Mode (no-op without a container).
 *   2. Mount the surface the core derives: nothing for `pending` / `none`,
 *      the vanilla banner for `banner`, the vanilla notice for `notice`.
 *   3. Mount/unmount the vanilla Customize modal from `customizeOpen`.
 *   4. Gate every ERB `a[data-cookie-preferences]` (paint-only
 *      `aria-disabled`) until `defaultsSettled`, and append the
 *      "GPC active" chip while `gpcActive`.
 *
 * Capability paths (utils/capabilityPaths.ts) boot none of this: gtm.js would
 * read the token in the URL, and nothing on those pages needs consent.
 */

import {
  initConsent,
  getState,
  subscribe,
  exposeGlobal,
  type ConsentState,
} from "../app/consent/core";
import { bootstrapGtm } from "../app/consent/gtm";
import { captureAuthTokenHandoff } from "../app/utils/authTokenHandoff";
import { isCapabilityPath } from "../app/utils/capabilityPaths";
import {
  mountBanner,
  syncGate,
  type BannerHandle,
} from "../app/consent/banner";
import { mountNotice, type NoticeHandle } from "../app/consent/notice";
import {
  mountCustomizeModal,
  type CustomizeModalHandle,
} from "../app/consent/customizeModal";

let bannerHandle: BannerHandle | null = null;
let noticeHandle: NoticeHandle | null = null;
let modalHandle: CustomizeModalHandle | null = null;

/**
 * Mount or unmount the banner and the notice from `state.surface`.
 * `pending` (D24: the resolved defaults or the reconcile are still in
 * flight) and `none` mount nothing. GPC no longer hides the banner (D18);
 * it only locks Advertising in the modal.
 *
 * @param state - current `ConsentState` snapshot
 */
function syncSurface(state: ConsentState): void {
  const showBanner = state.surface === "banner";
  const showNotice = state.surface === "notice";

  if (showBanner && !bannerHandle) {
    bannerHandle = mountBanner(document.body);
  } else if (!showBanner && bannerHandle) {
    bannerHandle.unmount();
    bannerHandle = null;
  }
  if (bannerHandle) syncGate(state);

  if (showNotice && !noticeHandle) {
    noticeHandle = mountNotice(document.body);
  } else if (!showNotice && noticeHandle) {
    noticeHandle.unmount();
    noticeHandle = null;
  }
}

/**
 * Mount or unmount the Customize modal depending on state.
 *
 * @param state - current `ConsentState` snapshot
 */
function syncModal(state: ConsentState): void {
  if (state.customizeOpen && !modalHandle) {
    modalHandle = mountCustomizeModal(document.body);
  } else if (!state.customizeOpen && modalHandle) {
    modalHandle.unmount();
    modalHandle = null;
  }
}

/**
 * Gate the ERB Cookie Preferences anchors and toggle their GPC chip. The
 * gate is paint-only (`aria-disabled` + `opacity-50 cursor-not-allowed`),
 * so the anchor keeps its box; its inline `showCustomize()` onclick stays
 * and is a no-op through the core's own `defaultsSettled` gate.
 *
 * @param state - current `ConsentState` snapshot
 */
function syncPreferencesAnchors(state: ConsentState): void {
  const gated = !state.defaultsSettled;
  document
    .querySelectorAll<HTMLAnchorElement>("a[data-cookie-preferences]")
    .forEach((anchor) => {
      if (gated) anchor.setAttribute("aria-disabled", "true");
      else anchor.removeAttribute("aria-disabled");
      anchor.classList.toggle("opacity-50", gated);
      anchor.classList.toggle("cursor-not-allowed", gated);

      const chip = anchor.querySelector(":scope > span[data-gpc-chip]");
      if (state.gpcActive && !chip) anchor.appendChild(buildGpcChip());
      else if (!state.gpcActive && chip) chip.remove();
    });
}

/**
 * Build the "GPC active" chip, with the same token classes as the SPA's
 * `CookiePreferencesLink` chip.
 *
 * @returns the chip element
 */
function buildGpcChip(): HTMLSpanElement {
  const chip = document.createElement("span");
  chip.setAttribute("data-gpc-chip", "");
  chip.setAttribute("aria-label", "Global Privacy Control is active");
  chip.title = "Global Privacy Control active - advertising cookies are off";
  chip.className =
    "ml-2 inline-flex items-center rounded-full border border-border-teal-200 bg-surface-teal-50 px-2 py-0.5 text-xs font-medium text-text-teal-700";
  chip.textContent = "GPC active";
  return chip;
}

function boot(): void {
  if (isCapabilityPath()) return;

  // Before bootstrapGtm(): the JWT must be off the URL before gtm.js loads.
  captureAuthTokenHandoff();
  exposeGlobal();
  initConsent();
  // After initConsent() so the stored decision and GPC are known; before the
  // surface subscribe() so `gtm.start` precedes any consent update.
  bootstrapGtm({ getState, subscribe });
  subscribe((state) => {
    syncSurface(state);
    syncModal(state);
    syncPreferencesAnchors(state);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
